/* eslint no-console: 0*/
/* global process */
/**
 * Exports the shared logging utility.
 *
 * @module tools/Logger
 */

import Bugsnag from '@bugsnag/js';
import { ConsoleFormattedStream, createLogger } from 'browser-bunyan';

import { getLogLevel } from '../lib/configuration/DebugSettingsUtil';

export function apiErrorSerializer(apiError) {
  if (typeof apiError !== 'object') {
    return apiError;
  }

  return { ...apiError };
}

class ClientStream extends ConsoleFormattedStream {
  write(rec) {
    rec.childName = rec.component;
    super.write(rec);
  }
}
class UnformattedConsoleStream {
  write(rec) {
    if (rec.obj) {
      console[rec.levelName](rec.msg, rec.obj);
    } else {
      console[rec.levelName](rec.msg);
    }
  }
}
class BugsnagStream {
  write(record) {
    if (!window.__CONFIG__.webMonitoringEnabled) {
      console.warn(
        'Attempting to start a Bugsnag logging stream without webMonitoringAppName or Bugsnag loaded',
      );
      return;
    }
    const isRecordErrorObj = record instanceof Error;
    let notifyParam;

    if (isRecordErrorObj) {
      notifyParam = record;
    } else {
      if (record.err && record.msg !== record.err.message) {
        record.msg = [record.err.message, record.msg].join(' - ');
      }

      notifyParam = record.msg;
    }

    Bugsnag.notify(notifyParam, event => {
      if (!isRecordErrorObj) {
        event.errors[0].stacktrace.splice(0, 3);
      }
      event.context = record.component;
      event.severity = record.level;
    });
  }
}

// Create the default log streams
const streams = [
  {
    level: getLogLevel(),
    stream:
      process.env.NODE_ENV === 'test'
        ? new UnformattedConsoleStream()
        : new ClientStream({ logByLevel: true }),
  },
];

// Add Bugsnag stream if it's enabled. Query the __CONFIG__ data directly since store data is not available yet
if (window.__CONFIG__.webMonitoringEnabled && process.env.NODE_ENV !== 'test') {
  const bugSnagStream = {
    level: window.__CONFIG__.webMonitoringLogLevel,
    stream: new BugsnagStream(),
  };

  streams.push(bugSnagStream);
}

/**
 * This is the root logger for the application. Depending on build-time variables it will include the client-side
 * logging stream and/or the remote logging stream. The expected usage of the logger would be to import the log
 * into your own module and create a "child logger" with a "component" key for mapping/filtering logging output.
 * Beyond this notion, there are multiple ways in which the logger can be used.
 *
 * The allowed log level is defined at build time with the variable "logLevel" in the configuration and can be one
 * of trace, debug, info, warn, error, fatal.
 *
 * ```javascript
 * // a simple usage example
 * import Logger from './tools/Logger';
 *
 * const log = Logger.child({component: 'Store'});
 * // ...
 * log.info('initialized');
 * // -> [14:21:51:06] INFO: Client/Store: initialized
 * ```
 *
 * Here are a few things to remember:
 * - When calling the `child` method on the logger, any properties you pass in will be preserved across all calls
 *   to the child logger
 * - When calling any of the log methods (trace,debug,info,warn,error,fatal) on your logger, to output additional
 *   debugging data supply it as a named value of an object as the first parameter
 *   e.g. log.trace({payload: payload, requestToken: tok});
 * - When passing additional data for output use "trace"
 * - You can also inline data using the special `%j` in your message and ordering additional parameters to the logger
 *   e.g. log.info('returned %j', ret);
 * - The `info` level is for generally useful information (even outside of the module/components own context)
 * - For information only useful when debugging a particular component use `debug`
 * - For additional debugging information/spam use `trace`
 */
const log = createLogger({
  name: 'client',
  streams,
  serializers: {
    apiError: apiErrorSerializer,
  },
  src: process.env.NODE_ENV === 'development' ? true : false,
});

window.logger = log;

export default log;
